import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ReceiptLong as RegisterIcon,
} from '@mui/icons-material';

/**
 * REPORTING-CENTER.2 — help modal for the Inventory Transaction Register and
 * its single-item Stock Card, following the InventoryHelpModal.tsx idiom.
 */

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const TransactionRegisterHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)' } },
      }}
      PaperProps={{
        sx: {
          background: isDark ? 'rgba(22, 22, 35, 0.90)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RegisterIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Transaction Register Guide
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The stock ledger and stock card
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          The <strong>Transaction Register</strong> is a line-by-line ledger of every stock movement —
          received, transferred, sold, adjusted, wasted — with In and Out quantities in separate
          columns, filterable by date, location, movement type, category and item.
        </Typography>

        <Divider />

        <Note>
          <strong>Stock Card.</strong> Click any row&apos;s item (or filter to a single item) to open its
          Stock Card: the same ledger scoped to that one item, ordered <strong>oldest first</strong> so
          the <strong>Opening Balance</strong> line sits at the top, the <strong>Running Balance</strong>{' '}
          grows down after each movement, and the <strong>Closing Balance</strong> sits at the bottom.
          Opening is the ledger sum of everything before your start date. (The full register, by
          contrast, lists movements newest first.)
        </Note>

        <Note variant="warning">
          <strong>Dates are entry time (postedAt), not business dates.</strong> A movement appears on
          the day it was <em>recorded</em>. Imported sales are posted at import time, so a January
          sales file entered in March lands in March here.
        </Note>

        <Note>
          <strong>Location filter caveat.</strong> Filtering by location excludes movements recorded
          without a location. Venue-wide (no location filter) includes every movement.
        </Note>

        <Note>
          <strong>Recorded vs estimated cost.</strong> Once cost tracking is active, <strong>Unit Cost</strong>
          and <strong>Value</strong> show the cost actually recorded when each movement was posted. Rows shown
          in a lighter colour with a dotted underline are <em>estimated</em> — either opening stock valued when
          tracking started, or stock with no recorded purchase cost (valued at the last known cost); hover to
          see why. A dash (&ldquo;—&rdquo;) means no cost was recorded at all. Movements dated before cost
          tracking was switched on are valued at today&apos;s cost, and a note above the table says from when
          values are recorded. <strong>Sale Price</strong> is the item&apos;s current list price. Cost columns
          are hidden for users without stock-cost access, and exports carry a <strong>Cost Basis</strong>
          column plus the activation date.
        </Note>

        <Note variant="warning">
          <strong>Est. Revenue is indicative.</strong> It is quantity × the item&apos;s <em>current list
          price</em>, shown on sale and sale-reversal rows only — <strong>not</strong> actual POS revenue,
          and not the price charged at the time of sale. Treat it as a rough indicator until historical
          costing and real POS pricing land.
        </Note>

        <Note>
          <strong>Exports.</strong> Download buttons export the full filtered set (not just the current
          page) as XLSX or CSV, with your filters written into the header. Very large exports are
          capped — a warning appears when that happens so you can narrow the range.
        </Note>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionRegisterHelpModal;
